import Head from 'next/head';
import { LinkProps } from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { FunctionComponent } from 'react';

import useLoggedUser from '../../../../hooks/use-logged-user';
import usePageViewEvent from '../../../../hooks/use-page-view-event';
import useOGMeta from '../../../../utils/seo/ogmeta';
import useSchema from '../../../../utils/seo/schema-org/schema';
import useHreflang from '../../../../utils/seo/hreflang';
import { useAppContext } from '../../../providers/app-context';

const BrandsSchema: FunctionComponent<{
  hasSelection: any;
  partsInfo: any;
  breadcrumbs: Array<{
    href: LinkProps | LinkProps['href'];
    as?: LinkProps['as'];
    text: string;
  }>;
  translatedSlugs: any;
  count: string;
  selection: any;
}> = ({
  hasSelection,
  partsInfo,
  breadcrumbs,
  translatedSlugs,
  count,
  selection,
}) => {
  const { locale = 'en', pathname, query, asPath } = useRouter();
  const localePath = locale !== 'en' ? `/${locale}` : '';
  const { t } = useTranslation();
  const user = useLoggedUser();
  const { businessValues } = useAppContext();
  const subCategoryPlural = `parts-and-categories-plural:parts.${selection?.category.category}.${partsInfo?.subCategoryEnPart}`;

  const name = hasSelection
    ? t('brands:head.with-part.title', {
        part: partsInfo?.subCategoryEnPart
          ? t(
              subCategoryPlural,
              {},
              {
                fallback: partsInfo?.subCategory,
              }
            )
          : partsInfo?.subCategory,
        count,
        usedWord:
          partsInfo?.subCategoryType && partsInfo?.subCategoryType === 'F'
            ? t('grammar:words.used.feminine')
            : t('grammar:words.used.masculine'),
        totalStockAvailable: businessValues.totalStockAvailable.defaultValue,
      })
    : t('brands:head.no-part.title', {
        totalStockAvailable: businessValues.totalStockAvailable.defaultValue,
      });

  const description = hasSelection
    ? t('brands:head.with-part.description', {
        part: partsInfo?.subCategoryEnPart
          ? t(
              subCategoryPlural,
              {},
              {
                fallback: partsInfo?.subCategory,
              }
            )
          : partsInfo?.subCategory,
        count,
        yoursWord:
          partsInfo?.subCategoryType && partsInfo?.subCategoryType === 'F'
            ? t('grammar:pronnouns.yours.feminine')
            : t('grammar:pronnouns.yours.masculine'),
        usedWord:
          partsInfo?.subCategoryType && partsInfo?.subCategoryType === 'F'
            ? t('grammar:words.used.feminine')
            : t('grammar:words.used.masculine'),
        totalStockAvailable: businessValues.totalStockAvailable.defaultValue,
      })
    : t('brands:head.no-part.description', {
        totalStockAvailable: businessValues.totalStockAvailable.defaultValue,
      });

  const alternateLinks = useHreflang({
    locale,
    route: pathname.includes('[subCategory]') ? 'brands-with-part' : 'brands',
    params: query,
    translatedSlugs: translatedSlugs?.slugs,
  });

  usePageViewEvent(name);

  const og = useOGMeta({
    title: name,
    description,
    url: process.env.NEXT_PUBLIC_HOST + localePath + asPath,
  });

  return (
    <Head>
      <title>{name}</title>
      <meta name="description" key="description" content={description} />
      {alternateLinks}
      {og}

      <script {...useSchema({ name, description, breadcrumbs })} />
    </Head>
  );
};

export default BrandsSchema;
